import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from "@heroui/react";
import { useDispatch, useSelector } from "react-redux";
import { pushUndo, buildUndoEntry, selectUndoSyncToken } from "../redux/undo";
import {
  createHighlight,
  deleteHighlight,
  listHighlights,
  updateHighlight,
} from "../services/transcriptionHighlights";

// Shared, persistent highlights for a single transcription. The provider
// owns the list and exposes CRUD helpers + a per-paragraph selector so
// rendering code doesn't have to do its own filtering on every word render.
const HighlightsContext = createContext(null);

// Palette is defined here so every chooser, badge, and waveform tick uses
// the same set. Add a value here and it shows up everywhere.
export const HIGHLIGHT_PALETTE = [
  { id: "yellow", color: "#fde047", label: "Gelb" },
  { id: "green", color: "#86efac", label: "Grün" },
  { id: "blue", color: "#93c5fd", label: "Blau" },
  { id: "pink", color: "#f9a8d4", label: "Pink" },
  { id: "orange", color: "#fdba74", label: "Orange" },
  { id: "purple", color: "#c4b5fd", label: "Lila" },
];

export const HighlightsProvider = ({ transcriptionId, children }) => {
  const dispatch = useDispatch();
  const [highlights, setHighlights] = useState([]);
  // Mirror of `highlights` for the undo recorders: they run inside
  // useCallback and must read the current list without taking it as a
  // dependency, which would rebuild every handler on each change.
  const highlightsRef = useRef([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  // Delete-confirm modal state lives on the provider so the same modal
  // serves every call site (overview panel row, in-transcript ✕ button,
  // anywhere else later) without each maintaining its own copy.
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const reload = useCallback(async () => {
    if (transcriptionId == null) {
      setHighlights([]);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const list = await listHighlights(transcriptionId);
      setHighlights(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Failed to load highlights:", err);
      setError(err.message || "Konnte Hervorhebungen nicht laden");
      setHighlights([]);
    } finally {
      setIsLoading(false);
    }
  }, [transcriptionId]);

  useEffect(() => {
    highlightsRef.current = highlights;
  }, [highlights]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Undo writes highlights straight to the server; this list is local state,
  // so it has to be re-read or the change is invisible until a page reload.
  const undoSyncToken = useSelector(selectUndoSyncToken);
  const undoSyncSeenRef = useRef(undoSyncToken);
  useEffect(() => {
    if (undoSyncToken === undoSyncSeenRef.current) return;
    undoSyncSeenRef.current = undoSyncToken;
    reload();
  }, [undoSyncToken, reload]);

  const addHighlight = useCallback(
    async ({ startOffset, endOffset, color, snippet, note }) => {
      if (transcriptionId == null) return null;
      const payload = {
        transcription_id: Number(transcriptionId),
        start_offset: Number(startOffset),
        end_offset: Number(endOffset),
        color,
        snippet,
        note,
      };
      const created = await createHighlight(payload);
      dispatch(pushUndo(buildUndoEntry({
        type: "highlight-add",
        label: "Hervorhebung hinzufügen",
        sessionId: transcriptionId,
        before: [],
        after: [],
        removeHighlights: created?.id != null ? [created.id] : [],
      })));
      // Optimistic-ish: trust the server response and merge it in sorted by
      // start_offset so consumers don't have to re-sort.
      setHighlights((prev) => {
        const next = [...prev, created];
        next.sort((a, b) => (a.start_offset ?? 0) - (b.start_offset ?? 0));
        return next;
      });
      return created;
    },
    [transcriptionId]
  );

  const removeHighlight = useCallback(async (id) => {
    // Capture the row before it goes, so undo can recreate it.
    const gone = highlightsRef.current.find((h) => h.id === id);
    await deleteHighlight(id);
    setHighlights((prev) => prev.filter((h) => h.id !== id));
    if (gone) {
      dispatch(pushUndo(buildUndoEntry({
        type: "highlight-remove",
        label: "Hervorhebung löschen",
        sessionId: transcriptionId,
        before: [],
        after: [],
        recreateHighlights: [gone],
      })));
    }
  }, [dispatch, transcriptionId]);

  const patchHighlight = useCallback(async (id, updates) => {
    const previous = highlightsRef.current.find((h) => h.id === id);
    const updated = await updateHighlight(id, updates);
    if (previous) {
      dispatch(pushUndo(buildUndoEntry({
        type: "highlight-edit",
        label: "Hervorhebung ändern",
        sessionId: transcriptionId,
        before: [],
        after: [],
        restoreHighlights: [previous],
      })));
    }
    setHighlights((prev) =>
      prev
        .map((h) => (h.id === id ? updated : h))
        .sort((a, b) => (a.start_offset ?? 0) - (b.start_offset ?? 0))
    );
    return updated;
  }, [dispatch, transcriptionId]);

  // Opens the shared confirm modal. Callers don't await this — the
  // actual delete happens inside the modal's confirm handler.
  const requestDeleteHighlight = useCallback((id) => {
    if (id == null) return;
    setPendingDeleteId(id);
  }, []);

  const cancelDelete = useCallback(() => {
    if (isDeleting) return;
    setPendingDeleteId(null);
  }, [isDeleting]);

  const confirmDelete = useCallback(async () => {
    if (pendingDeleteId == null || isDeleting) return;
    setIsDeleting(true);
    try {
      await removeHighlight(pendingDeleteId);
      setPendingDeleteId(null);
    } catch (err) {
      // Keep the modal open on error so the user sees what happened.
      console.error("Failed to delete highlight:", err);
    } finally {
      setIsDeleting(false);
    }
  }, [pendingDeleteId, isDeleting, removeHighlight]);

  const pendingHighlight =
    pendingDeleteId != null
      ? highlights.find((h) => h.id === pendingDeleteId)
      : null;

  const value = useMemo(
    () => ({
      highlights,
      isLoading,
      error,
      reload,
      addHighlight,
      removeHighlight,
      patchHighlight,
      requestDeleteHighlight,
    }),
    [
      highlights,
      isLoading,
      error,
      reload,
      addHighlight,
      removeHighlight,
      patchHighlight,
      requestDeleteHighlight,
    ]
  );

  return (
    <HighlightsContext.Provider value={value}>
      {children}
      <Modal
        isOpen={pendingDeleteId != null}
        onOpenChange={(open) => {
          if (!open) cancelDelete();
        }}
        size="md"
        placement="center"
        isDismissable={!isDeleting}
        hideCloseButton={isDeleting}
      >
        <ModalContent>
          {() => (
            <>
              <ModalHeader>Hervorhebung löschen</ModalHeader>
              <ModalBody>
                <p className="text-sm text-onSurface/80">
                  Diese Hervorhebung wirklich löschen? Die Aktion kann nicht
                  rückgängig gemacht werden.
                </p>
                {pendingHighlight ? (
                  <div className="mt-3 flex items-start gap-2 rounded-md bg-surfaceContainer p-2">
                    <span
                      className="mt-1 inline-block h-3 w-3 flex-shrink-0 rounded-full ring-1 ring-gray-300"
                      style={{ backgroundColor: pendingHighlight.color }}
                      aria-hidden="true"
                    />
                    <span className="text-sm text-onSurface">
                      {pendingHighlight.snippet || (
                        <span className="italic text-onSurface/60">
                          (kein Text)
                        </span>
                      )}
                    </span>
                  </div>
                ) : null}
              </ModalBody>
              <ModalFooter>
                <Button
                  variant="bordered"
                  color="default"
                  radius="full"
                  onPress={cancelDelete}
                  isDisabled={isDeleting}
                >
                  Abbrechen
                </Button>
                <Button
                  color="danger"
                  onPress={confirmDelete}
                  isLoading={isDeleting}
                >
                  Löschen
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </HighlightsContext.Provider>
  );
};

export const useHighlights = () => {
  const ctx = useContext(HighlightsContext);
  if (!ctx) {
    // Defensive: components that mount outside the provider just see an
    // empty list and no-ops, so they degrade gracefully instead of crashing.
    return {
      highlights: [],
      isLoading: false,
      error: null,
      reload: async () => {},
      addHighlight: async () => null,
      removeHighlight: async () => {},
      patchHighlight: async () => null,
      requestDeleteHighlight: () => {},
    };
  }
  return ctx;
};

// Returns the subset of highlights that overlap [start, end). Use this in
// per-paragraph renderers to keep word-span work O(highlights_in_paragraph)
// instead of O(highlights_in_session).
export const useHighlightsInRange = (start, end) => {
  const { highlights } = useHighlights();
  return useMemo(() => {
    if (!Number.isFinite(start)) return [];
    const upper = Number.isFinite(end) ? end : Infinity;
    return highlights.filter((h) => {
      const a = Number(h.start_offset);
      const b = Number(h.end_offset);
      if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
      // Overlap test for half-open intervals [a, b] vs [start, upper).
      return b >= start && a < upper;
    });
  }, [highlights, start, end]);
};
